/**
 * Renders an audit-log entry as one sentence (user-management §4.10
 * "Audit log (rendered lines)"), from the entry and the users the endpoint
 * returned. Names that are gone fall back to "someone" rather than an ID.
 */
import {
  AuditLogEvent,
  ChannelType,
  NxDataFix,
  type AuditLogEntry,
  type Snowflake,
  type User,
} from '@neosocial/protocol';
import type { Translator } from '../i18n';
import { FSI, listFormat, PDI } from '../i18n/core';
import type { DataState } from '../store/reducer';
import { displayName } from '../store/selectors';
import { threadAuditLine } from '../threads/audit';
import { AUTOMOD_AUDIT_EVENTS, automodAuditLine } from './automodText';
import { eventAuditLine, EVENT_AUDIT_FILTERS } from '../events/audit';

export interface AuditContext {
  d: DataState;
  guildId: Snowflake;
  /** From `AuditLogResponse.users`. */
  users: Record<Snowflake, User>;
}

function change(e: AuditLogEntry, key: string) {
  return e.changes?.find((c) => c.key === key);
}

function str(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

export function auditUser(ctx: AuditContext, tr: Translator, id: Snowflake | null): string {
  if (!id) return tr.t('um.audit.system');
  const u = ctx.users[id] ?? ctx.d.users[id];
  const m = ctx.d.members[ctx.guildId]?.[id];
  return u ? displayName(u, m) : tr.t('um.audit.someone');
}

/** Channel types Discord writes with '#': text, announcement, forum and media channels, threads. */
const HASH_TYPES: ReadonlySet<number> = new Set([
  ChannelType.GUILD_TEXT,
  ChannelType.GUILD_ANNOUNCEMENT,
  ChannelType.ANNOUNCEMENT_THREAD,
  ChannelType.PUBLIC_THREAD,
  ChannelType.PRIVATE_THREAD,
  ChannelType.GUILD_FORUM,
  ChannelType.GUILD_MEDIA,
]);

/** Entries whose channel is always a voice one, or always a text one. */
const VOICE_ACTIONS: ReadonlySet<number> = new Set([
  AuditLogEvent.MEMBER_MOVE,
  AuditLogEvent.VOICE_CHANNEL_STATUS_UPDATE,
  AuditLogEvent.VOICE_CHANNEL_STATUS_DELETE,
]);
const TEXT_ACTIONS: ReadonlySet<number> = new Set([
  AuditLogEvent.MESSAGE_DELETE,
  AuditLogEvent.MESSAGE_PIN,
  AuditLogEvent.MESSAGE_UNPIN,
  AuditLogEvent.NX_POLL_END,
]);

/**
 * Whether a channel is written with '#': its type as the client knows it,
 * else as the entry stored it (a deleted channel's `type` change), else
 * what the action implies. Unknown: no '#' (never a text channel's mark on
 * a voice channel).
 */
function hashed(ctx: AuditContext, e: AuditLogEntry, cid: string | null): boolean {
  const known = cid ? ctx.d.channels[cid]?.type : undefined;
  const stored = change(e, 'type');
  const type = known ?? stored?.new_value ?? stored?.old_value;
  if (typeof type === 'number') return HASH_TYPES.has(type);
  if (VOICE_ACTIONS.has(e.action_type)) return false;
  return TEXT_ACTIONS.has(e.action_type);
}

/**
 * A channel's name in a line: '#general' for a text channel, a voice or
 * stage channel (or a category) by its name alone, as Discord writes them
 * (QA 2026-10-28 round 4: "moved Bahar to #Room 2").
 */
function channelName(ctx: AuditContext, e: AuditLogEntry, id?: string | null): string {
  const cid = id ?? e.options?.channel_id ?? null;
  const c = cid ? ctx.d.channels[cid] : undefined;
  const name =
    c?.name ||
    (change(e, 'name')?.new_value as string | undefined) ||
    (change(e, 'name')?.old_value as string | undefined) ||
    '—';
  // left-to-right isolate: '#' stays before the name inside right-to-left text
  return hashed(ctx, e, cid) ? '⁦#' + name + '⁩' : name;
}

/**
 * A voice channel's name as it reads in a sentence ("in Room 1"): no '#'
 * (`interpolate` isolates it, so a Latin name keeps its order inside
 * right-to-left text). The name the channel has now, else the one the entry stored
 * when it was written (`options.nx_channel_name`: a deleted channel keeps
 * its name), else a neutral "a channel": a channel missing from the store
 * may be one the reader cannot see, not a deleted one (QA 2026-10-28 round
 * 1: both read "a deleted channel").
 */
function voiceChannelName(ctx: AuditContext, tr: Translator, e: AuditLogEntry): string {
  const id = e.options?.channel_id ?? null;
  const name = id ? ctx.d.channels[id]?.name : undefined;
  return name || e.options?.nx_channel_name || tr.t('um.audit.aChannel');
}

function roleName(ctx: AuditContext, e: AuditLogEntry, id?: string | null): string {
  const rid = id ?? e.target_id;
  const r = ctx.d.guilds[ctx.guildId]?.roles.find((x) => x.id === rid);
  return (
    r?.name ??
    (change(e, 'name')?.new_value as string | undefined) ??
    (change(e, 'name')?.old_value as string | undefined) ??
    '—'
  );
}

/** "A and B", "A, B, and C". */
function listOf(_tr: Translator, items: string[]): string {
  return listFormat(items);
}

/** A stand-in no name can hold (names have no control characters). */
const slot = (k: string) => `\u0000${k}\u0000`;

/**
 * `t(key, vars)` with `phrases` put in as they are: phrases built here,
 * whose names are isolated one by one already. `t` isolates each value it
 * fills in; a second isolate around a whole phrase would keep a right-to-left
 * sentence from ordering "A و B" right to left.
 */
function withPhrases(
  tr: Translator,
  key: string,
  vars: Record<string, string | number>,
  phrases: Record<string, string>,
): string {
  const slots = Object.fromEntries(Object.keys(phrases).map((k) => [k, slot(k)]));
  let s = tr.t(key, { ...vars, ...slots });
  for (const [k, p] of Object.entries(phrases)) s = s.split(FSI + slot(k) + PDI).join(p);
  return s;
}

/**
 * The roles of a MEMBER_ROLE_UPDATE `$add` / `$remove`, as one phrase, or
 * null when there are none. Discord's shape is [{id, name}], what the server
 * stores since 2026-10-28; entries stored before hold the plain ids (QA
 * 2026-10-28 round 3: those read as "—"). A name comes from the entry, else
 * the role as the server has it now; a role that is neither is a deleted
 * one, said as such, never as a name (QA 2026-10-28 follow-up round 2:
 * "removed the role a deleted role"). One role reads "the role A", several
 * "the roles A and B" (a deleted one among them counts), and deleted roles
 * alone "a deleted role" / "2 deleted roles".
 */
function rolesPhrase(ctx: AuditContext, tr: Translator, v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const roles = ctx.d.guilds[ctx.guildId]?.roles ?? [];
  const named: string[] = [];
  let deleted = 0;
  for (const x of v as unknown[]) {
    const ref =
      typeof x === 'string'
        ? { id: x, name: '' }
        : x && typeof x === 'object'
          ? (x as { id?: unknown; name?: unknown })
          : {};
    const stored = typeof ref.name === 'string' ? ref.name : '';
    const now = typeof ref.id === 'string' ? roles.find((r) => r.id === ref.id)?.name : undefined;
    const name = stored || now;
    if (name) named.push(FSI + name + PDI);
    else deleted++;
  }
  const gone = deleted ? [tr.t('um.audit.roles.deleted', { n: deleted })] : [];
  if (!named.length) return gone[0] ?? null;
  return withPhrases(
    tr,
    'um.audit.roles.named',
    { n: named.length + deleted },
    { roles: listOf(tr, [...named, ...gone]) },
  );
}

/** The sentence for one entry. */
export function auditLine(ctx: AuditContext, tr: Translator, e: AuditLogEntry): string {
  const { t, fmt } = tr;
  // a console action names no staff member: "Neosocial staff" (Console v2 decision 2)
  const actor = e.nx_source === 'console' ? tr.t('sv.audit.staff') : auditUser(ctx, tr, e.user_id);
  const target = auditUser(ctx, tr, e.target_id);
  const time = (v: unknown) => (typeof v === 'string' && v ? fmt.stamp(v) : '—');
  // AutoMod (2026-10-29): its own lines (automodText.ts)
  if (AUTOMOD_AUDIT_EVENTS.has(e.action_type))
    return automodAuditLine(tr, e, { actor, target, channel: channelName(ctx, e), time });
  switch (e.action_type as number) {
    case AuditLogEvent.MEMBER_KICK:
      return t('audit.kick', { actor, target });
    case AuditLogEvent.MEMBER_BAN_ADD: {
      const until = change(e, 'nx_expires_at')?.new_value ?? e.options?.nx_expires_at;
      return until
        ? t('audit.ban', { actor, target, time: time(until) })
        : t('audit.banPerm', { actor, target });
    }
    case AuditLogEvent.MEMBER_BAN_REMOVE:
      return t('audit.unban', { actor, target });
    case AuditLogEvent.MEMBER_UPDATE: {
      const to = change(e, 'communication_disabled_until');
      if (to)
        return to.new_value
          ? t('audit.timeout', { actor, target, time: time(to.new_value) })
          : t('audit.untimeout', { actor, target });
      const nick = change(e, 'nick');
      if (nick)
        return t('audit.nick', {
          actor,
          target,
          old: str(nick.old_value),
          new: str(nick.new_value),
        });
      const mute = change(e, 'mute');
      if (mute)
        return mute.new_value
          ? t('audit.mute', { actor, target })
          : t('um.audit.unmute', { actor, target });
      const deaf = change(e, 'deaf');
      if (deaf)
        return deaf.new_value
          ? t('um.audit.deaf', { actor, target })
          : t('um.audit.undeaf', { actor, target });
      // a moderator's mute / deafen in one voice channel (2026-10-28)
      const chMute = change(e, 'nx_channel_mute');
      if (chMute)
        return t(chMute.new_value ? 'um.audit.channelMute' : 'um.audit.channelUnmute', {
          actor,
          target,
          channel: voiceChannelName(ctx, tr, e),
        });
      const chDeaf = change(e, 'nx_channel_deaf');
      if (chDeaf)
        return t(chDeaf.new_value ? 'um.audit.channelDeaf' : 'um.audit.channelUndeaf', {
          actor,
          target,
          channel: voiceChannelName(ctx, tr, e),
        });
      return t('um.audit.memberUpdate', { actor, target });
    }
    case AuditLogEvent.MEMBER_ROLE_UPDATE: {
      const added = rolesPhrase(ctx, tr, change(e, '$add')?.new_value);
      const removed = rolesPhrase(ctx, tr, change(e, '$remove')?.new_value);
      if (added && removed)
        return withPhrases(tr, 'audit.roleBoth', { actor, target }, { added, removed });
      return added
        ? withPhrases(tr, 'audit.roleAdd', { actor, target }, { roles: added })
        : withPhrases(tr, 'audit.roleRemove', { actor, target }, { roles: removed ?? '—' });
    }
    case AuditLogEvent.MEMBER_MOVE:
      // AFK auto-move (2026-11-01): the server moved them, as the system
      if (e.options?.nx_afk) return t('vx.audit.afkMove', { target, channel: channelName(ctx, e) });
      return t('audit.move', { actor, target, channel: channelName(ctx, e) });
    // voice channel status (2026-11-01, Discord's 192 / 193)
    case AuditLogEvent.VOICE_CHANNEL_STATUS_UPDATE:
      return t('vx.audit.status', { actor, channel: channelName(ctx, e, e.target_id) });
    case AuditLogEvent.VOICE_CHANNEL_STATUS_DELETE:
      return t('vx.audit.statusClear', { actor, channel: channelName(ctx, e, e.target_id) });
    case AuditLogEvent.MEMBER_DISCONNECT:
      return t('audit.disconnect', { actor, target });
    case AuditLogEvent.ROLE_CREATE:
      return t('um.audit.roleCreate', { actor, role: roleName(ctx, e) });
    case AuditLogEvent.ROLE_UPDATE:
      return t('audit.roleUpdate', { actor, role: roleName(ctx, e) });
    case AuditLogEvent.ROLE_DELETE:
      return t('um.audit.roleDelete', { actor, role: roleName(ctx, e) });
    case AuditLogEvent.CHANNEL_CREATE:
      return t('um.audit.channelCreate', { actor, target: channelName(ctx, e, e.target_id) });
    // custom emoji (2026-11-01): by the name the entry stored
    case AuditLogEvent.EMOJI_CREATE:
      return t('emo.audit.create', { actor, name: str(change(e, 'name')?.new_value) });
    case AuditLogEvent.EMOJI_UPDATE:
      return t('emo.audit.update', {
        actor,
        old: str(change(e, 'name')?.old_value),
        name: str(change(e, 'name')?.new_value),
      });
    case AuditLogEvent.EMOJI_DELETE:
      return t('emo.audit.delete', { actor, name: str(change(e, 'name')?.old_value) });
    case AuditLogEvent.CHANNEL_UPDATE:
      return t('um.audit.channelUpdate', { actor, target: channelName(ctx, e, e.target_id) });
    case AuditLogEvent.CHANNEL_DELETE:
      return t('um.audit.channelDelete', { actor, target: channelName(ctx, e, e.target_id) });
    case AuditLogEvent.CHANNEL_OVERWRITE_CREATE:
    case AuditLogEvent.CHANNEL_OVERWRITE_UPDATE:
    case AuditLogEvent.CHANNEL_OVERWRITE_DELETE: {
      const id = e.options?.id ?? null;
      const who =
        e.options?.type === '0' || e.options?.type === 'role'
          ? id === ctx.guildId
            ? '@everyone'
            : roleName(ctx, e, id)
          : auditUser(ctx, tr, id);
      return t('audit.overwrite', {
        actor,
        target: who,
        channel: channelName(ctx, e, e.target_id),
      });
    }
    case AuditLogEvent.INVITE_CREATE:
      return t('audit.invite', { actor, code: str(change(e, 'code')?.new_value) });
    case AuditLogEvent.INVITE_DELETE:
      return t('um.audit.inviteDelete', { actor, code: str(change(e, 'code')?.old_value) });
    case AuditLogEvent.MESSAGE_DELETE:
      return t('audit.msgDelete', { actor, target, channel: channelName(ctx, e) });
    case AuditLogEvent.MESSAGE_PIN:
      return t('um.audit.pin', { actor, target, channel: channelName(ctx, e) });
    case AuditLogEvent.MESSAGE_UNPIN:
      return t('um.audit.unpin', { actor, target, channel: channelName(ctx, e) });
    // threads (2026-10-29): the thread is the target, its channel options.channel_id
    case AuditLogEvent.THREAD_CREATE:
    case AuditLogEvent.THREAD_UPDATE:
    case AuditLogEvent.THREAD_DELETE:
      return threadAuditLine(tr, e, {
        actor,
        storeName: e.target_id ? ctx.d.channels[e.target_id]?.name : null,
        channel: channelName(ctx, { ...e, changes: [] }, e.options?.channel_id ?? null),
      });
    case AuditLogEvent.NX_POLL_END:
      return t('um.audit.pollEnd', { actor, target, channel: channelName(ctx, e) });
    case AuditLogEvent.NX_CHANNEL_BAN_ADD: {
      const until = change(e, 'expires_at')?.new_value ?? e.options?.expires_at;
      return until
        ? t('audit.channelBan', { actor, target, channel: channelName(ctx, e), time: time(until) })
        : t('um.audit.channelBanPerm', { actor, target, channel: channelName(ctx, e) });
    }
    case AuditLogEvent.NX_CHANNEL_PASSWORD_UPDATE:
      return t(
        change(e, 'nx_has_password')?.new_value === false
          ? 'um.audit.passwordOff'
          : 'um.audit.passwordOn',
        { actor, channel: channelName(ctx, e, e.target_id) },
      );
    case AuditLogEvent.NX_CHANNEL_BAN_REMOVE:
      return t('audit.channelUnban', { actor, target, channel: channelName(ctx, e) });
    case AuditLogEvent.NX_CHANNEL_RESTORE:
      return t('audit.restore', { actor, target: channelName(ctx, e, e.target_id) });
    case AuditLogEvent.NX_ROLE_RESTORE:
      return t('audit.restore', { actor, target: roleName(ctx, e) });
    // scheduled events (2026-10-29): the name it had when written
    case AuditLogEvent.GUILD_SCHEDULED_EVENT_CREATE:
    case AuditLogEvent.GUILD_SCHEDULED_EVENT_UPDATE:
    case AuditLogEvent.GUILD_SCHEDULED_EVENT_DELETE:
      return eventAuditLine(tr, e, actor);
    case AuditLogEvent.GUILD_UPDATE: {
      // ownership transfers: options.nx_owner_transfer = started | cancelled | completed
      const step = e.options?.nx_owner_transfer;
      const owner = change(e, 'owner_id');
      if (step === 'started' && owner?.new_value)
        return t('audit.ownerTransfer', {
          actor,
          target: auditUser(ctx, tr, String(owner.new_value)),
          date: e.options?.effective_at ? fmt.date(String(e.options.effective_at)) : '—',
        });
      if (step === 'cancelled') return t('um.audit.ownerTransferCancel', { actor });
      if (owner?.new_value)
        return t('audit.ownerTransferDone', {
          target: auditUser(ctx, tr, String(owner.new_value)),
        });
      return t('um.audit.guildUpdate', { actor });
    }
    default:
      return t('um.audit.generic', { actor, action: String(e.action_type) });
  }
}

/**
 * The "via …" note for an entry's source, or null for none. The server
 * stores `app` for every action a person takes from their session (QA
 * 2026-10-28 round 3: it stored `api`, so everything read "via API"). Old
 * entries keep their stored `hub` / `comms` source (Hub only's decision
 * record, "Old data"); they read "via the app" too, so the retired mode's
 * name never shows (QA 2026-10-28 round 2). `system` is the server itself.
 *
 * A stored `api` reads "via the app" too (QA 2026-10-28 follow-up round
 * 3): no API caller has ever existed (the server's `AuditSource`), so every
 * `api` entry is an action a person took in the app before the server
 * stored `app` for them. The stored rows are left as they are.
 */
const APP_SOURCES: ReadonlySet<string> = new Set(['app', 'hub', 'comms', 'api']);

export function auditSourceKey(source: AuditLogEntry['nx_source']): string | null {
  if (!source) return null;
  // Neosocial staff acting from the console (Console v2 decision 2)
  if (source === 'console') return 'sv.audit.via';
  return APP_SOURCES.has(source) ? 'um.audit.src.app' : `um.audit.src.${source}`;
}

/** Why a start-up data fix wrote an entry (`options.nx_data_fix`), by the fix. */
const DATA_FIX_REASONS: Readonly<Record<string, string>> = {
  [NxDataFix.RETIRE_TALK_POWER]: 'um.audit.fix.noSpeak',
  [NxDataFix.RENAME_SEEDED_SQUADS]: 'um.audit.fix.rename',
  [NxDataFix.REMOVE_SEEDED_CLANS]: 'um.audit.fix.remove',
  [NxDataFix.ENGLISH_ONLY]: 'um.audit.fix.english',
};

/**
 * The English reasons the same fixes stored before the marker (servers that
 * ran them then keep these entries), matched exactly.
 */
const LEGACY_FIX_REASONS: Readonly<Record<string, string>> = {
  'Talk power is retired (2026-10-28): the channel denies @everyone Speak instead of needing talk power.':
    'um.audit.fix.noSpeak',
  'No squads (2026-10-28): the seeded voice channel is renamed.': 'um.audit.fix.rename',
  'No clans (owner decision 2026-10-28): the seeded Clans channels are removed.':
    'um.audit.fix.remove',
};

/**
 * An entry's reason as the viewer reads it, or null for none. A data fix's
 * entry names its fix (`options.nx_data_fix`), and its reason is said in the
 * viewer's language, in Hub's words; the English sentences older builds
 * stored for those fixes read the same way (QA 2026-10-28 round 4: the
 * owner's Audit log said "Talk power is retired…" in English, whatever
 * the UI said). Anyone else's reason is what they wrote.
 */
export function auditReason(tr: Translator, e: AuditLogEntry): string | null {
  const fix = e.options?.nx_data_fix;
  const key = fix
    ? (DATA_FIX_REASONS[fix] ?? 'um.audit.fix.other')
    : e.reason
      ? LEGACY_FIX_REASONS[e.reason]
      : undefined;
  if (key) return tr.t(key, { role: '@everyone' });
  return e.reason || null;
}

/** The action filters the viewer offers (Discord's codes). */
export const AUDIT_FILTERS: Array<{ key: string; type: number }> = [
  { key: 'um.audit.f.kick', type: AuditLogEvent.MEMBER_KICK },
  { key: 'um.audit.f.ban', type: AuditLogEvent.MEMBER_BAN_ADD },
  { key: 'um.audit.f.unban', type: AuditLogEvent.MEMBER_BAN_REMOVE },
  { key: 'um.audit.f.member', type: AuditLogEvent.MEMBER_UPDATE },
  { key: 'um.audit.f.roles', type: AuditLogEvent.MEMBER_ROLE_UPDATE },
  { key: 'um.audit.f.move', type: AuditLogEvent.MEMBER_MOVE },
  { key: 'um.audit.f.disconnect', type: AuditLogEvent.MEMBER_DISCONNECT },
  { key: 'um.audit.f.channel', type: AuditLogEvent.CHANNEL_UPDATE },
  { key: 'vx.audit.f.status', type: AuditLogEvent.VOICE_CHANNEL_STATUS_UPDATE },
  { key: 'um.audit.f.role', type: AuditLogEvent.ROLE_UPDATE },
  { key: 'um.audit.f.invite', type: AuditLogEvent.INVITE_CREATE },
  { key: 'um.audit.f.message', type: AuditLogEvent.MESSAGE_DELETE },
  { key: 'th.audit.f', type: AuditLogEvent.THREAD_UPDATE },
  { key: 'emo.audit.f.create', type: AuditLogEvent.EMOJI_CREATE },
  { key: 'emo.audit.f.update', type: AuditLogEvent.EMOJI_UPDATE },
  { key: 'emo.audit.f.delete', type: AuditLogEvent.EMOJI_DELETE },
  { key: 'am.audit.f', type: AuditLogEvent.AUTO_MODERATION_BLOCK_MESSAGE },
  ...EVENT_AUDIT_FILTERS,
];
